import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type ThemeMode = "light" | "dark";
/**
 * How much of the product is pointed at you at once. This is Binance's
 * Lite/Pro split rather than a feature tier — see `shell/experience.ts` for the
 * rule it follows (a level hides tools, never data).
 */
export type ExperienceLevel = "starter" | "pro";

export interface Preferences {
  theme: ThemeMode;
  experience: ExperienceLevel;
}

const STORAGE_KEY = "strat.prefs";
/** Pre-rename, theme-only key. Read once so an existing choice survives. */
const LEGACY_THEME_KEY = "wsb-theme";

function systemTheme(): ThemeMode {
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function readStoredPrefs(): Preferences {
  const fallback: Preferences = {
    theme: systemTheme(),
    // Starter by default: someone opening this for the first time is better
    // served by the short bar, and the switch is one click from the header.
    experience: "starter",
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Preferences>;
      return {
        theme: parsed.theme === "light" || parsed.theme === "dark" ? parsed.theme : fallback.theme,
        experience: parsed.experience === "pro" ? "pro" : "starter",
      };
    }
    const legacyTheme = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacyTheme === "light" || legacyTheme === "dark") {
      return { ...fallback, theme: legacyTheme };
    }
  } catch {
    // A corrupt or unavailable store costs the user their preferences and
    // nothing else — the defaults above are all usable.
  }
  return fallback;
}

/**
 * Writes the style-driving axis onto <html>, where the stylesheet reads it.
 * Exported because index.html runs the same logic inline before first paint:
 * applied only after React mounts, a non-default theme visibly repaints the
 * whole page on every load.
 */
export function applyPrefsToDocument(prefs: Preferences): void {
  document.documentElement.dataset.theme = prefs.theme;
}

interface PreferencesContextValue {
  prefs: Preferences;
  update: (patch: Partial<Preferences>) => void;
  toggleTheme: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Preferences>(readStoredPrefs);

  useEffect(() => {
    applyPrefsToDocument(prefs);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Private-mode quota failures shouldn't break the page.
    }
  }, [prefs]);

  const update = useCallback((patch: Partial<Preferences>) => {
    setPrefs((current) => ({ ...current, ...patch }));
  }, []);

  const value = useMemo(
    () => ({
      prefs,
      update,
      toggleTheme: () => update({ theme: prefs.theme === "dark" ? "light" : "dark" }),
    }),
    [prefs, update],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used inside PreferencesProvider");
  return ctx;
}

/** Just the colour axis — what the Plotly charts need, since they can't read CSS. */
export function useTheme(): { mode: ThemeMode; toggle: () => void } {
  const { prefs, toggleTheme } = usePreferences();
  return { mode: prefs.theme, toggle: toggleTheme };
}

/** True when the UI should present its simplified, fewer-choices form. */
export function useIsStarter(): boolean {
  return usePreferences().prefs.experience === "starter";
}
